import React, { useRef, useState } from 'react';

import { InputPdfUpload } from '.';
import FieldErrorMessage from './FieldErrorMessage';

interface PDFUploadFieldProps {
  label?: string;
  errors?: string[];
  onFileUpload?: (file: File) => void;
  onDelete?: (fileUrl: string) => void;
  preview?: string | null;
  originalFileName?: string;
  isUploading?: boolean;
  removeButtonText?: string;
  updateButtonText?: string;
  maxFileSize?: number;
}

const PDFUploadField: React.FC<PDFUploadFieldProps> = ({
  label = 'PDF',
  errors,
  onFileUpload,
  onDelete,
  preview,
  originalFileName,
  isUploading = false,
  removeButtonText = '削除する',
  updateButtonText = '変更する',
  maxFileSize,
}) => {
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [newErrors, setNewErrors] = useState<string[]>(errors ?? []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      if (maxFileSize && file.size > maxFileSize) {
        setNewErrors([`PDF は ${maxFileSize / 1024 / 1024} MB以下のファイルを設定してください。`]);
        return;
      }
      onFileUpload?.(file);
      setSelectedFileName(file.name);
      setNewErrors([]);
    } else {
      setSelectedFileName(null);
      setNewErrors(['PDFファイルを選択してください']);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = async () => {
    if (preview && onDelete) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFileName(null);
      await onDelete(preview);
    }
  };

  const fileName = originalFileName || preview?.split('/').pop();

  return (
    <div className='flex flex-col gap-[6px]'>
      <p className='text-sm'>{label}</p>
      <input
        type='file'
        ref={fileInputRef}
        accept='.pdf'
        onChange={handleFileChange}
        className='hidden'
        data-testid='input-pdf-upload'
      />
      <div className='flex flex-col gap-2'>
        {!preview && (
          <button
            onClick={handleButtonClick}
            className='flex cursor-pointer items-baseline justify-center rounded border border-solid border-primary-500 bg-white px-2 py-[10px] text-sm text-primary transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50'
            type='button'
          >
            <span className='truncate'>PDFを選択</span>
          </button>
        )}
        {preview && (
          <>
            <InputPdfUpload>{selectedFileName || fileName}</InputPdfUpload>
            <div className='flex items-baseline gap-2'>
              <button
                onClick={handleButtonClick}
                className='text-xs text-primary-500 underline hover:no-underline'
                type='button'
                disabled={isUploading}
              >
                {updateButtonText}
              </button>
              <button
                onClick={handleDelete}
                className='text-xs text-primary-500 underline hover:no-underline'
                type='button'
                disabled={isUploading}
              >
                {removeButtonText}
              </button>
            </div>
          </>
        )}
      </div>
      <FieldErrorMessage errors={newErrors} />
    </div>
  );
};

export default PDFUploadField;
